import React, { useState } from 'react';
import { ChartData } from '../../types/analytics';
import { Chart } from '../shared/Charts/Chart';
import { getTransferTypeColor } from '../../utils/colorThemes';

export interface ActivityChartsProps {
  data: ChartData;
  loading?: boolean;
}

const ActivityCharts: React.FC<ActivityChartsProps> = ({ data, loading = false }) => {
  const [activeChart, setActiveChart] = useState<'partners' | 'tasks'>('partners');

  const formatPartnerChartData = () => {
    return data.mostActivePartners.map(partner => ({
      name: partner.partnerName,
      'Inbound': partner.transferCounts.inbound,
      'Outbound': partner.transferCounts.outbound,
      total: partner.total
    }));
  };

  const formatTaskChartData = () => {
    return data.mostActiveTasks.map(task => ({
      name: `${task.taskName}`,
      partner: task.partnerName,
      executions: task.executionCount,
      'Success Rate': Math.round(task.successRate),
      transferDirection: task.transferDirection
    }));
  };

  const partnerChartColors = [
    getTransferTypeColor('Inbound'),
    getTransferTypeColor('Outbound'),
  ];
  const taskChartColors = ['#17a2b8', '#6f42c1', '#e83e8c', '#fd7e14'];

  const formatTooltipValue = (value: any, name: string) => {
    if (name === 'Success Rate') {
      return [`${value}%`, name];
    }
    return [value.toLocaleString(), name];
  };

  const formatXAxisLabel = (value: string) => {
    return value.length > 15 ? `${value.substring(0, 12)}...` : value;
  };

  if (loading) {
    return (
      <div className="p-5 bg-white dark:bg-slate-800 rounded-lg opacity-70">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-neutral-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-slate-50">
            Activity Analytics
          </h3>
          <div className="h-8 w-48 bg-neutral-200 dark:bg-slate-700 rounded animate-pulse" />
        </div>
        <div className="min-h-[400px] bg-neutral-100 dark:bg-slate-900 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-5 bg-white dark:bg-slate-800 rounded-lg">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 pb-4 border-b border-neutral-200 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-slate-50">
          Activity Analytics ({data.timeRange})
        </h3>

        {/* Tab buttons */}
        <div className="flex gap-1 p-1 bg-neutral-100 dark:bg-slate-700 rounded-md">
          <button
            className={`px-4 py-2 text-sm font-medium rounded transition-all whitespace-nowrap ${
              activeChart === 'partners'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-neutral-600 dark:text-slate-400 hover:bg-neutral-200 dark:hover:bg-slate-600 hover:text-neutral-900 dark:hover:text-slate-200'
            }`}
            onClick={() => setActiveChart('partners')}
          >
            Most Active Partners
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium rounded transition-all whitespace-nowrap ${
              activeChart === 'tasks'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-neutral-600 dark:text-slate-400 hover:bg-neutral-200 dark:hover:bg-slate-600 hover:text-neutral-900 dark:hover:text-slate-200'
            }`}
            onClick={() => setActiveChart('tasks')}
          >
            Most Active Tasks
          </button>
        </div>
      </div>

      {/* Chart content */}
      <div className="min-h-[400px]">
        {activeChart === 'partners' ? (
          <div className="flex flex-col gap-4">
            {/* Chart info */}
            <div className="text-center mb-4">
              <h4 className="text-base font-semibold text-neutral-900 dark:text-slate-50 mb-1">
                Partner Transfer Activity
              </h4>
              <p className="text-sm text-neutral-500 dark:text-slate-400">
                Transfer counts by type for the most active partners
              </p>
            </div>

            {/* Chart container - dark background in dark mode */}
            <div className="p-4 bg-neutral-50 dark:bg-slate-900 border border-neutral-200 dark:border-slate-700 rounded-md">
              <Chart
                data={formatPartnerChartData()}
                type="bar"
                colors={partnerChartColors}
                height={400}
                responsive={true}
                showTooltip={true}
                showLegend={true}
                showGrid={true}
                animate={true}
                xAxisKey="name"
                series={[
                  { key: 'Inbound', name: 'Inbound', color: partnerChartColors[0] },
                  { key: 'Outbound', name: 'Outbound', color: partnerChartColors[1] },
                ]}
                formatXAxis={formatXAxisLabel}
                formatYAxis={(value) => value.toLocaleString()}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                enableExport={true}
                showExportButtons={true}
                exportFilename="partner-activity-chart"
                enableBrush={true}
                tooltipFormatter={(value, name) => [value.toLocaleString(), name]}
                tooltipLabelFormatter={(label) => `Partner: ${label}`}
                responsiveBreakpoints={{
                  mobile: 768,
                  tablet: 1024,
                  desktop: 1200
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Chart info */}
            <div className="text-center mb-4">
              <h4 className="text-base font-semibold text-neutral-900 dark:text-slate-50 mb-1">
                Task Execution Activity
              </h4>
              <p className="text-sm text-neutral-500 dark:text-slate-400">
                Execution counts and success rates for the most active tasks
              </p>
            </div>

            {/* Chart container - dark background in dark mode */}
            <div className="p-4 bg-neutral-50 dark:bg-slate-900 border border-neutral-200 dark:border-slate-700 rounded-md">
              <Chart
                data={formatTaskChartData()}
                type="combo"
                colors={taskChartColors}
                height={400}
                responsive={true}
                showTooltip={true}
                showLegend={true}
                showGrid={true}
                animate={true}
                xAxisKey="name"
                series={[
                  { key: 'executions', name: 'Executions', type: 'bar', color: taskChartColors[0] },
                  { key: 'Success Rate', name: 'Success Rate (%)', type: 'line', color: taskChartColors[1] }
                ]}
                formatXAxis={formatXAxisLabel}
                formatYAxis={(value) => value.toLocaleString()}
                customTooltip={(props: any) => {
                  const { active, payload, label } = props;
                  if (active && payload && payload.length) {
                    const tooltipData = payload[0].payload;
                    return (
                      <div className="bg-white dark:bg-slate-800 border border-neutral-200 dark:border-slate-700 rounded-md p-3 shadow-lg text-sm">
                        <p className="font-semibold text-neutral-900 dark:text-slate-50 mb-1">{label}</p>
                        <p className="text-neutral-500 dark:text-slate-400 text-xs mb-1">
                          Partner: {tooltipData.partner}
                        </p>
                        <p className="text-neutral-500 dark:text-slate-400 text-xs mb-2">
                          Direction: {tooltipData.transferDirection}
                        </p>
                        {payload.map((entry: any, index: number) => (
                          <p key={index} style={{ color: entry.color }}>
                            {`${entry.name}: ${formatTooltipValue(entry.value, entry.name)[0]}`}
                          </p>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                enableExport={true}
                showExportButtons={true}
                exportFilename="task-activity-chart"
                enableBrush={true}
                enableZoom={true}
                enablePan={true}
                responsiveBreakpoints={{
                  mobile: 768,
                  tablet: 1024,
                  desktop: 1200
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityCharts;
